/**
 * RWBR — the EZRA BOTTOM GROUP SURGE (BGS).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE SOURCE, AND IT IS ONE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `EZRAS2RulesFinal.docx`, §3.13, printed under the heading ***EZRA BOTTOM GROUP
 * SURGE***, p.20 per the rules index. The section is SHORT and it is transcribed
 * here in full, in the order it is printed, with nothing left out — because the
 * whole argument of this file is about which of these sentences is complete and
 * which one is not:
 *
 *   *"A Bottom Group Surge (BGS) can occur on any Pit or Final race turn."*
 *
 *   *"It is not a turn type but rather a Prelude Action that will precede any
 *    turn if allowed by race circumstances and a random dice roll."*
 *
 *   *"An BGS is activated by a D10 roll on the EZRA PRELUDE TRIGGER TABLE
 *    (PTT). (See Rules Appendix.)"*
 *
 *   *"BGS's are activated before the running of the regular turn. The regular
 *    turn will follow the completion of the BGS and the filling of any gaps that
 *    might result (RB/vi)."*
 *
 *   *"All cars in BG, including the LDG sub-group are eligible."*
 *
 *   *"Use the Driver Finder Table to select the surging driver. If the Driver
 *    Finder Table selects a grid position empty due to race attrition, re-roll on
 *    the appropriate table. If three re-rolls fail to select an occupied BG grid
 *    position the surge does not occur."*
 *
 *   *"If the driver is in the LLSG he surges to the BG lead position (20th). All
 *    drivers passed by the surging driver displace back one position to fill the
 *    empty space. **If the driver occupies the 20th position he is awarded a BG
 *    performance chip.**"* (the last sentence is italicised in the original)
 *
 *   *"If the driver is in the LDSG he surges to escape the LDSG becoming the last
 *    car in the LLSG. All LDSG drivers passed by the surging driver displace back
 *    one position to fill the empty space. If the surging driver was in the LDSG
 *    top position simply move the LDSG marker to the car occupying the second
 *    LDSG position."*
 *
 *   *"BG= Bottom Group"*
 *   *"LLSG = Lead Lap Sub-Group: BG cars on the lead lap."*
 *   *"LDSG = Lap Down Sub-Group: BG cars designated as a lap (laps) down and
 *    trailing the LLSG."*
 *
 * And the one sentence about the Surge that is printed OUTSIDE §3.13, in §3.12:
 *
 *   *"When a BGC is activated, all other aspects of that turn as they affect the
 *    Top, Middle and Bottom Groups, including a Bottom Group Surge, are resolved
 *    after the BGC has completed."*
 *
 * That is the entire Surge. Eight sentences of procedure, all of them complete,
 * and one sentence of trigger that names a die and a table and no face.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-35 — ⚠ OPEN RULING: THE TRIGGER FACE IS NOT PRINTED. THE RULE IS OFF.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **This is the one thing in this file that is a guess, it is flagged as one, and
 * it is switched off until Howard rules.** Everything else here is printed text.
 *
 * ── WHAT §3.13 ACTUALLY SAYS ───────────────────────────────────────────────
 *
 *   *"An BGS is activated by a D10 roll on the EZRA PRELUDE TRIGGER TABLE (PTT).
 *    (See Rules Appendix.)"*
 *
 * A die. A table. **No face.** Compare the two other preludes in the same
 * document, both of which name theirs:
 *
 *   §3.12 Churn    *"An BGC is activated by a D10 roll **of 1** on the EZRA
 *                   PRELUDE TRIGGER TABLE (PTT)."*
 *   §3.6 Restart   *"Activated on any 'Restart Turn' when a prelude **D10 Roll =
 *                   1** followed by a positive decider die roll."*
 *
 * ── WHERE THIS WAS LOOKED FOR, SO NOBODY HAS TO LOOK AGAIN ─────────────────
 *
 * The full `EZRAS2RulesFinal.docx` was read end to end, not searched:
 *
 *   • §3.13 itself and the sentences either side of it. §3.12 ends on the "all
 *     other aspects ... including a Bottom Group Surge" note; §3.13 follows;
 *     §3.14 Final Turn Rules follows that. Neither neighbour mentions a face.
 *   • The document's own **Rules Appendix**, which §3.13 points at. The index
 *     lists exactly four: Appendix A (Speed Qualifying Calculations), B1 (Pit
 *     Violation Chart), B2 (Pit Stop Trouble Chart), C (Accidents, Crashes and
 *     Wrecks Charts). **There is no PRELUDE TRIGGER TABLE in the appendix, in the
 *     index, or anywhere else in the document.** The three appendix headings that
 *     do appear at the foot of the file are headings only, with no charts under
 *     them.
 *   • The rules INDEX, in case the PTT were a numbered section. It is not.
 *   • The other prelude sections (§3.6 Restart Procedures, §3.12) in case one of
 *     them reproduced the table. Neither does; both name their own face in prose
 *     and cite the PTT the same way §3.13 does.
 *   • `CROSS_REFERENCE.md` B8 already records the PTT as "not in any supplied
 *     document".
 *
 * The omission is very unlikely to be a typo. §3.13 scopes itself to *"any Pit or
 * Final race turn"* where §3.12 takes *"any Regular or Final race turn"* — the
 * two preludes are **disjoint by design**, meeting only on a Final turn, so there
 * is no argument from "they are the same rule, so they share a number".
 *
 * ── WHAT WOULD CLOSE THIS ──────────────────────────────────────────────────
 *
 * Any one of:
 *   1. The EZRA PRELUDE TRIGGER TABLE itself, from the Rules Appendix of a
 *      complete copy of the S2 rules, or from any EZRA supplement that reprints
 *      it. That is the primary source and it ends the question outright.
 *   2. A sentence anywhere in the EZRA corpus of the form "a D10 roll of N" (or a
 *      range) attached to the Surge.
 *   3. Howard simply ruling it, which is a legitimate close for a house rule —
 *      recorded as a house ruling rather than as a citation, and this comment
 *      updated to say which it was.
 *
 * D-EZ-1 (*"where an EZRA add-on is silent, the base RWBR rulebook governs"*)
 * gives nothing at all here: **the base rulebook has no Surge.** There is no
 * fallback to fall back to, which is precisely why this has to be an open ruling
 * rather than a derivation.
 *
 * ── THE CANDIDATE READINGS ─────────────────────────────────────────────────
 *
 * There is a useful structural fact that narrows the question a long way: **on a
 * d10, every single-face reading is the same rate.** So the candidates are not
 * ten different numbers, they are three:
 *
 *   (A) ONE FACE, whichever it is → **10.000% of eligible turns.** If the PTT is
 *       a routing table (one d10 rolled before every turn, the face looked up to
 *       see which prelude, if any, is eligible), a single face per prelude is its
 *       natural shape. Face 1 is the only face the corpus ever attaches to a
 *       prelude, and it does so twice (§3.12, §3.6) — which is the reason the
 *       DEFAULT below is 1. Note that 1 cannot be the Surge's face if the PTT is
 *       a routing table read on a Final turn, because a Final turn is eligible
 *       for BOTH preludes and one face cannot route to two. On Pit turns, where
 *       the Churn is excluded outright, face 1 routing to the Surge is perfectly
 *       coherent.
 *   (B) A RANGE, e.g. 1-2 or 9-10 → **20%**, or wider. A table worth printing
 *       probably does assign more than one face to something, and the Restart
 *       table in §3.6 is exactly a d10 with ranges on it (2-5 Restart Violation,
 *       etc.). This is the reading that actually changes the answer, and it is
 *       the reason the option below takes a LIST of faces rather than one number.
 *   (C) A MODIFIED roll, in the shape of the Late Turn Extra Action Phase: *"an
 *       EAP is conducted on a d10 roll of 10. This die roll is modified by +1
 *       when..."*. If the Surge is like that, its rate is conditional on race
 *       state and no flat percentage describes it.
 *
 * **The rate is a guess until Howard rules.** Under the shipped default it is
 * 10% of Pit and Final turns; under (B) it could be double that; under (C) it is
 * not a constant at all. `npm run surge` prints the measured rate against
 * whatever is configured, next to all three closed forms, so the size of the
 * question is on screen every time anybody looks.
 *
 * ── THEREFORE: OFF ─────────────────────────────────────────────────────────
 *
 * `BGS_DEFAULT_ENABLED = false`. `race.ts` builds no `SurgeTracker` unless asked,
 * `turn.ts` rolls no die, and a race run today is byte-identical to a race run
 * before this file existed. An off-by-default rule that can be switched on is
 * honest. An on-by-default invented rate would silently change every race in the
 * game — and it would do it in the most damaging place, because a Surge hands a
 * bottom-group car a free run to 20th and sometimes a performance chip with no
 * roll to earn it and no way for anybody to defend against it.
 *
 * Switch it on the same way every other EZRA option is switched, through
 * `RaceOptions`:
 *
 *   runRace(setup, { surge: true })                  // reading (A), face 1
 *   runRace(setup, { surge: { faces: [1, 2] } })     // reading (B), 20%
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-36 — A YELLOW FLAG DOES NOT BLOCK A SURGE (unlike the Churn)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D-EZ-30 excludes the Churn from a yellow-flag turn, and the reasoning there is
 * not a reading of the word "Regular" — it is that the Churn's FIRST STEP is
 * *"Roll Race Cam Dice"* and RB/xi says *"In a YELLOW FLAG turn, no RACE CAM
 * action occurs"*, so step one cannot be performed.
 *
 * **That argument does not transfer, and it must not be copied over out of
 * symmetry.** The Surge reads no race cam. Its only die is the Driver Finder's
 * 11-66, it makes no challenge, and it resolves no incident. Every step of it is
 * performable under caution.
 *
 * So the printed scope stands unmodified: *"any Pit or Final race turn"*, yellow
 * or green. §3.13 excludes nothing, and inventing an exclusion would suppress a
 * printed rule — which is the same class of error as inventing a result, just in
 * the other direction.
 *
 * It is worth noting how often this bites, because it is not a corner: RB/xi
 * pairs pit stops with cautions constantly (*"If a YELLOW FLAG turn occurs on a
 * PIT TURN, conduct the pit stop"*), so a large share of all Pit turns are run
 * under yellow. `npm run surge` prints the split.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-37 — A SURGE OUT OF THE LDSG IS NOT GATED BY THE PIT CREW LADDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §3.11 gates a laps-down car's escape on its PIT CREW grade:
 *
 *   *"subject to Pit crew rating (A-B-C automatic, D has to successfully
 *    Challenge twice, E can never challenge out of the LDSG)."*
 *
 * §3.13's LDSG branch says nothing about the grade. Ruled: **the ladder does not
 * apply to a Surge**, on three legs.
 *
 *   1. The ladder is written about CHALLENGING, in the words it uses twice —
 *      "has to successfully Challenge twice", "can never challenge out". A Surge
 *      is not a challenge; there is no defender and no red-white-blue roll.
 *   2. There is already a printed free escape that ignores the ladder, and this
 *      engine already implements it that way: the **Lucky Dog**. It hands the
 *      first laps-down car its lap back with no roll and no grade check
 *      (`awardLuckyDog` in `lapsDown.ts` never reads `pitCrewGrade`). A Surge is
 *      the same shape of rule — a free positional gift, selected by a table — so
 *      gating it would make the engine treat two printed free passes
 *      inconsistently.
 *   3. §3.13 is the later and more specific section, and it describes the LDSG
 *      escape in full, including what happens to the marker. A section that
 *      spells out the mechanics that carefully and omits a gate is not relying on
 *      the reader to import one.
 *
 * ⚠ THE ARGUMENT AGAINST, RECORDED SO IT CAN BE WON LATER. The Laps Down
 * Procedure's Pit Crew Grade Effects section is broader than the §3.11 bullet:
 * *"A PCG of E prevents escape from the 'laps down' sub-group. The unfortunate
 * driver simply limps around the track until the race ends or is he removed from
 * the race by a subsequent event."* Read on its own that bars a grade-E car from
 * escaping by ANY route, Surge and Lucky Dog alike. This engine has already ruled
 * against that reading for the Lucky Dog; the Surge follows the same ruling for
 * consistency rather than making a fresh one. `npm run surge` counts grade-E
 * surge escapes separately, so if Howard prefers the broader reading the cost of
 * the change is a number that is already on screen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-38 — PRELUDE ORDER: CHURN, THEN SURGE, THEN THE RACE START EVENT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Churn-before-Surge is printed: *"all other aspects of that turn ... including a
 * Bottom Group Surge, are resolved after the BGC has completed"*. Nothing is
 * printed about Surge versus Race Start Event, and both call themselves preludes
 * resolved "before the running of the regular turn".
 *
 * **Ruled, as an engine ordering decision with no printed basis: the Surge runs
 * immediately after the Churn and before the RSE.** The Churn's sentence names
 * the Surge as the thing that follows it, §3.11-3.13 are one block of BOTTOM
 * GROUP rules, and keeping the pair adjacent means the bottom group is settled
 * once rather than half-settled, disturbed by a field-wide prelude, and settled
 * again.
 *
 * WHEN IT MATTERS: only on a turn that is both a Pit turn and a Restart turn —
 * which the Restart Supplement makes a real combination (*"A Restart Turn is
 * executed at the beginning of a Pit Turn that immediately follows a Yellow Flag
 * Turn"*) — or on a Final turn that is also a Restart turn. `npm run surge`
 * counts those co-occurrences so the question's size is visible rather than
 * assumed to be small.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  TWO SENTENCES THAT TURN OUT TO NEED NO CODE, AND WHY THAT IS NOT AN OMISSION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *"the filling of any gaps that might result (RB/vi)"* — a Surge produces no
 * gap. It lifts exactly one card out of the running order and puts it back in at
 * another index; the cards it passed shuffle back one to close the hole behind
 * it, which is the same sentence read from the other end (*"All drivers passed by
 * the surging driver displace back one position to fill the empty space"*).
 * Nobody leaves the board, so there is never a hole for RB/vi to fill. The clause
 * is precautionary, and a FILL A GAP call here would invent movement.
 *
 * *Yellow flags.* The Churn needs a whole deferral mechanism because its
 * challenges can produce triples, triangles and TROUBLE. A Surge rolls the Driver
 * Finder and nothing else — no cam, no challenge, no escape roll — so no incident
 * of any kind can arise from it and there is nothing to defer.
 */

import type { Car } from './types.ts';
import { groupOf } from './types.ts';
import type { Dice } from './dice.ts';
import type { DiceRecord, Modifier, RuleEvent } from './ruleEvent.ts';
import { diffOrder, nameOf } from './ruleEvent.ts';
import { awardBgChip, bgChipsOf, type BgChipRefusal } from './chips.ts';
import { rollDriverFinder } from './problem.ts';
import { troubleFieldFromGrid, type TroubleField } from './trouble.ts';
import { returnToLeadLap, type LapsDownTracker } from './lapsDown.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_BGS =
  'EZRA-S2 §3.13 Bottom Group Surge — "A Bottom Group Surge (BGS) can occur on any Pit or Final ' +
  'race turn. It is not a turn type but rather a Prelude Action that will precede any turn if ' +
  'allowed by race circumstances and a random dice roll."';

/**
 * ⚠ THE OPEN RULING, as a string, so it can be cited from the ledger, from a
 * test and from the harness rather than living only in a comment nobody runs.
 *
 * Every Surge event carries it. If a Surge ever appears in a race record, this
 * sentence appears next to it.
 */
export const CITE_BGS_TRIGGER_OPEN_RULING =
  '⚠ OPEN RULING D-EZ-35 — EZRA-S2 §3.13 prints NO trigger face: "An BGS is activated by a D10 ' +
  'roll on the EZRA PRELUDE TRIGGER TABLE (PTT). (See Rules Appendix.)" The PTT is in no supplied ' +
  'document (CROSS_REFERENCE B8) and the Rules Appendix index lists only A, B1, B2 and C. Compare ' +
  '§3.12, which prints "a D10 roll of 1", and §3.6, which prints "a prelude D10 Roll = 1". The ' +
  'default here is face 1 — the only face the corpus ever attaches to a prelude — and it is a ' +
  'GUESS: THE RATE IS UNRULED. The rule therefore ships OFF (BGS_DEFAULT_ENABLED = false) and is ' +
  'turned on only by an explicit `surge` option. Awaiting Howard\'s ruling.';

export const CITE_BGS_WHEN =
  'EZRA-S2 §3.13 — "A Bottom Group Surge (BGS) can occur on any Pit or Final race turn." + D-EZ-36: ' +
  'a YELLOW FLAG turn is NOT excluded, because the Churn\'s exclusion (D-EZ-30) rests on RB/xi ' +
  'stopping the RACE CAM and the Surge reads no race cam.';

export const CITE_BGS_ELIGIBLE =
  'EZRA-S2 §3.13 — "All cars in BG, including the LDG sub-group are eligible."';

export const CITE_BGS_FINDER =
  'EZRA-S2 §3.13 — "Use the Driver Finder Table to select the surging driver. If the Driver Finder ' +
  'Table selects a grid position empty due to race attrition, re-roll on the appropriate table. If ' +
  'three re-rolls fail to select an occupied BG grid position the surge does not occur."';

export const CITE_BGS_LLSG =
  'EZRA-S2 §3.13 — "If the driver is in the LLSG he surges to the BG lead position (20th). All ' +
  'drivers passed by the surging driver displace back one position to fill the empty space."';

export const CITE_BGS_CHIP =
  'EZRA-S2 §3.13 — "If the driver occupies the 20th position he is awarded a BG performance chip." ' +
  '(The chip itself is D-EZ-39, in chips.ts: spendable only in the BOTTOM group and only during a ' +
  'Churn, destroyed on leaving the BOTTOM group.)';

export const CITE_BGS_LDSG =
  'EZRA-S2 §3.13 — "If the driver is in the LDSG he surges to escape the LDSG becoming the last car ' +
  'in the LLSG. All LDSG drivers passed by the surging driver displace back one position to fill ' +
  'the empty space. If the surging driver was in the LDSG top position simply move the LDSG marker ' +
  'to the car occupying the second LDSG position." + D-EZ-37: the §3.11 PIT CREW ladder does not ' +
  'gate a Surge — it is written about challenging, and the Lucky Dog (the other printed free ' +
  'escape) is not gated either.';

export const CITE_BGS_ORDER =
  'EZRA-S2 §3.12 — "When a BGC is activated, all other aspects of that turn as they affect the Top, ' +
  'Middle and Bottom Groups, including a Bottom Group Surge, are resolved after the BGC has ' +
  'completed." + D-EZ-38 for the (unprinted) order against the Race Start Event.';

// ---------------------------------------------------------------------------
// The trigger — ⚠ OPEN RULING D-EZ-35
// ---------------------------------------------------------------------------

/** "An BGS is activated by a D10 roll..." The die IS printed. */
export const BGS_TRIGGER_SIDES = 10;

/**
 * ⚠ NOT PRINTED. The documented default face, awaiting Howard's ruling.
 *
 * 1 because it is the only face the corpus ever attaches to a prelude, and it
 * does so twice (§3.12's Churn, §3.6's Restart). That is a reason to prefer it
 * over any other single face; it is **not** evidence that §3.13 uses it. See
 * D-EZ-35 for the three candidate readings and what would close the question.
 */
export const BGS_TRIGGER_FACE_DEFAULT = 1;

/**
 * The faces that fire a Surge, as a LIST rather than a number.
 *
 * A list because candidate reading (B) is a RANGE — the §3.6 Restart table is
 * itself a d10 with ranges on it — and the shape of the code should not quietly
 * rule out the reading that would change the answer most.
 */
export const BGS_TRIGGER_FACES_DEFAULT: readonly number[] = Object.freeze([BGS_TRIGGER_FACE_DEFAULT]);

/**
 * ⚠ THE RULE IS OFF UNTIL HOWARD RULES ON THE TRIGGER.
 *
 * Read this constant as the answer to "has D-EZ-35 been closed?", because that
 * is what it means. Flipping it without a citation would be exactly the silent
 * invention the whole D-EZ-35 note exists to refuse.
 */
export const BGS_DEFAULT_ENABLED = false;

/** Ledger label for the Surge's own prelude die. */
export const BGS_TRIGGER_LABEL = 'BGS trigger (prelude) — ⚠ face is an OPEN RULING (D-EZ-35)';

/** "he surges to the BG lead position (20th)" — the first seat in the BOTTOM group. */
export const BG_LEAD_POSITION = 20;

/**
 * "If three re-rolls fail to select an occupied BG grid position the surge does
 * not occur." One roll plus three re-rolls is four rolls, and the count is
 * spelled out here rather than written as a literal 4 anywhere below so that the
 * arithmetic from the printed sentence is visible.
 */
export const BGS_FINDER_REROLLS = 3;
export const BGS_FINDER_MAX_ROLLS = 1 + BGS_FINDER_REROLLS;

/**
 * Is a Surge even eligible on this turn?
 *
 * *"can occur on any Pit or Final race turn"*, and D-EZ-36 for why a yellow flag
 * is not a bar. Pure, and separate from the roll, so the eligibility rule is
 * testable without spending a die — the same split `isChurnTurn` uses.
 */
export function isSurgeTurn(input: { isPitTurn: boolean; isFinalTurn: boolean }): boolean {
  return input.isPitTurn || input.isFinalTurn;
}

/**
 * Roll the trigger.
 *
 * `faces` is passed in rather than read from the constant, because the whole
 * point of D-EZ-35 is that the face is configuration and not knowledge. The
 * record's label carries the warning so a ledger row cannot be read as settled
 * rules.
 */
export function rollSurgeTrigger(
  dice: Dice,
  faces: readonly number[] = BGS_TRIGGER_FACES_DEFAULT,
): { fired: boolean; roll: number; record: DiceRecord } {
  const roll = dice.d10();
  const fired = faces.includes(roll);
  return {
    fired,
    roll,
    record: {
      kind: 'd10',
      dice: [roll],
      total: roll,
      label: BGS_TRIGGER_LABEL,
      flags: { fired, openRuling: true },
    },
  };
}

// ---------------------------------------------------------------------------
// The tracker
// ---------------------------------------------------------------------------

/**
 * The Surge's race-long bookkeeping.
 *
 * ⚠ NOTE WHAT IT DOES **NOT** CONTAIN: an occurrence limit. §3.12 caps the Churn
 * at *"once each one third race turn increment"*; §3.13 prints no limit of any
 * kind, and inventing one would be inventing a rate just as surely as inventing
 * a trigger face would. So a Surge can fire on every eligible turn, and this
 * class exists only to COUNT — for the harness, for `RaceResult`, and so that a
 * rule nobody can see fire cannot be mistaken for a rule that works.
 *
 * It also carries the configured trigger faces, which is why `turn.ts` takes an
 * object here rather than a boolean: the object IS the answer to "is D-EZ-35
 * closed, and how".
 */
export class SurgeTracker {
  /** The configured trigger faces (⚠ D-EZ-35 — configuration, not knowledge). */
  readonly faces: readonly number[];

  /** d10s that came up on a trigger face on an eligible turn. */
  triggered = 0;
  /** Surges that did nothing: no occupied BG slot in four Driver Finder rolls, or no BG at all. */
  aborted = 0;
  /** Surges that moved a car or awarded a chip. */
  occurred = 0;

  /** Occurred as an LLSG run to 20th. */
  llsgSurges = 0;
  /** Occurred as an LDSG escape onto the lead lap. */
  ldsgEscapes = 0;
  /** Occurred as "already on 20th" — a BG performance chip and no movement. */
  chipOnly = 0;
  /** BG performance chips actually handed over. */
  chipsAwarded = 0;
  /** Awards refused, at §3.1's cap of two. */
  chipsRefused = 0;
  /** Driver Finder rolls made, and how many named an empty slot. */
  finderRolls = 0;
  finderRejected = 0;
  /** D-EZ-37 — escapes by a car whose PIT CREW grade could never CHALLENGE out. */
  ungradedEscapes = 0;

  constructor(faces: readonly number[] = BGS_TRIGGER_FACES_DEFAULT) {
    if (faces.length === 0) throw new RangeError('a Surge trigger needs at least one d10 face');
    for (const f of faces) {
      if (!Number.isInteger(f) || f < 1 || f > BGS_TRIGGER_SIDES) {
        throw new RangeError(`${f} is not a face of a d${BGS_TRIGGER_SIDES}`);
      }
    }
    this.faces = Object.freeze([...faces]);
  }

  /** The configured rate, as a probability. Printed by the harness beside the measurement. */
  get triggerRate(): number {
    return this.faces.length / BGS_TRIGGER_SIDES;
  }

  /** Fold in one resolved Surge. */
  note(result: BottomGroupSurgeResult): void {
    this.finderRolls += result.finderAttempts.length;
    this.finderRejected += result.finderAttempts.filter((a) => !a.accepted).length;
    if (!result.occurred) {
      this.aborted += 1;
      return;
    }
    this.occurred += 1;
    if (result.momentTag === 'surge.chip') this.chipOnly += 1;
    if (result.momentTag === 'surge.llsg') this.llsgSurges += 1;
    if (result.momentTag === 'surge.ldsg') {
      this.ldsgEscapes += 1;
      if (result.escapedWithoutGrade) this.ungradedEscapes += 1;
    }
    this.chipsAwarded += result.awardedBgChip ? 1 : 0;
    this.chipsRefused += result.bgChipRefused === null ? 0 : 1;
  }
}

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

export type SurgeMoment =
  /** An LLSG car ran up to the BG lead position (20th). */
  | 'surge.llsg'
  /** The car named was already on 20th: "he is awarded a BG performance chip". */
  | 'surge.chip'
  /** An LDSG car escaped onto the lead lap. */
  | 'surge.ldsg'
  /** Started and did nothing — the finder never named an occupied BG slot. */
  | 'surge.aborted';

/** One Driver Finder roll, kept whether it was accepted or thrown away. */
export interface SurgeFinderAttempt {
  /** 1 = the first roll; 2-4 = the three printed re-rolls. */
  attempt: number;
  code: string;
  /** The BOTTOM group grid position the chart pointed at. */
  targetPosition: number;
  /** The car in that slot, or null when it was empty. */
  driverId: string | null;
  /**
   * *"If the Driver Finder Table selects a grid position empty due to race
   * attrition, re-roll"* — so an EXACT hit is required. `problem.ts` would
   * normally substitute the nearest occupied car, which is right for a PROBLEM
   * and wrong here, because §3.13 prints the re-roll instead.
   */
  accepted: boolean;
}

export interface BottomGroupSurgeResult extends RuleEvent {
  eventType: 'bottom-group-surge';
  momentTag: SurgeMoment;
  /** Every Driver Finder roll, in order, accepted and rejected alike. */
  finderAttempts: SurgeFinderAttempt[];
  /** The surging driver, or null when the finder never named one. */
  driverId: string | null;
  driverName: string | null;
  /** Where he was, and where he ended up. Equal when he was already on 20th. */
  from: number | null;
  to: number | null;
  /** Which sub-group he surged out of. */
  subGroup: 'LLSG' | 'LDSG' | null;
  /** "he is awarded a BG performance chip" (§3.13, and D-EZ-39 for the chip). */
  awardedBgChip: boolean;
  /** Set when the award was refused — §3.1's cap of two, or an impossible position. */
  bgChipRefused: BgChipRefusal | null;
  /** D-EZ-37 — this escape was by a car that could never have CHALLENGED out. */
  escapedWithoutGrade: boolean;
  /** Cars displaced back one position to close the hole behind the surging car. */
  passed: string[];
  /**
   * The running order the Surge left behind. **Authoritative** — the caller
   * installs this array rather than replaying `positionDeltas`, for the same
   * reason the Churn's is: a laps-down car who surged back onto the lead lap has
   * had `lapsDown` cleared and his parked trait chips handed back ON THE OBJECT
   * in here, and a caller that rebuilt the order from ids against its own copy of
   * the field would silently drop both.
   */
  order: Car[];
  /** The same order as ids, for the ledger and for tests. */
  finalOrder: string[];
  /** Did the Surge do anything? */
  occurred: boolean;
  /** Why it did not, when it did not. Reported rather than silently skipped. */
  abortReason: string | null;
}

export interface SurgeInput {
  /**
   * The running order, index 0 = leader. Cars still running only.
   *
   * `Car.position` is NOT read — array index is the running order, because the
   * stamped numbers are stale mid-turn and `renumber` does not run until the turn
   * concludes. Every position this module uses is derived from the array.
   */
  order: readonly Car[];
  dice: Dice;
  /** The LDSG's memory, or null when the laps-down optional rule is off. */
  lapsDown?: LapsDownTracker | null;
  /**
   * The prelude d10 that fired this Surge, so the ledger shows the roll that
   * caused the event. Passed in rather than rolled here because a caller has to
   * be able to ask "does a Surge happen?" without paying for the rest.
   */
  triggerRecord?: DiceRecord;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * A `TroubleField` view of a working order, positions taken from the array
 * rather than from `Car.position` — the cards have been moving and the stamped
 * numbers are stale until `renumber` runs at the end of the turn.
 */
function fieldOf(order: readonly Car[]): TroubleField {
  return troubleFieldFromGrid(order.map((c, i) => (c.position === i + 1 ? c : { ...c, position: i + 1 })));
}

function bottomGroupCount(order: readonly Car[]): number {
  return order.filter((_, i) => groupOf(i + 1) === 'bottom').length;
}

// ---------------------------------------------------------------------------
// The Surge
// ---------------------------------------------------------------------------

/**
 * Resolve a Bottom Group Surge that has already been triggered.
 *
 * The trigger is the caller's, for the reason `resolveBottomGroupChurn` splits
 * the same way and for one more: the trigger is the OPEN part (D-EZ-35) and the
 * procedure is the printed part, so keeping them in separate functions keeps the
 * guess out of the code that is not guessing.
 */
export function resolveBottomGroupSurge(input: SurgeInput): BottomGroupSurgeResult {
  const { dice } = input;
  const startOrder = [...input.order];
  const work = [...startOrder];
  const diceRecords: DiceRecord[] = input.triggerRecord ? [input.triggerRecord] : [];
  const modifiers: Modifier[] = [];
  const finderAttempts: SurgeFinderAttempt[] = [];

  const finish = (
    momentTag: SurgeMoment,
    description: string,
    extra: {
      driverId?: string | null;
      driverName?: string | null;
      from?: number | null;
      to?: number | null;
      subGroup?: 'LLSG' | 'LDSG' | null;
      awardedBgChip?: boolean;
      bgChipRefused?: BgChipRefusal | null;
      escapedWithoutGrade?: boolean;
      passed?: string[];
      occurred: boolean;
      abortReason?: string | null;
    },
  ): BottomGroupSurgeResult => ({
    eventType: 'bottom-group-surge',
    momentTag,
    // ⚠ Every Surge event carries the open ruling. A race record that contains a
    // Surge must say, on the event itself, that its rate is unruled.
    ruleCitation: `${CITE_BGS}; ${CITE_BGS_TRIGGER_OPEN_RULING}`,
    actorIds: extra.driverId ? [extra.driverId] : [],
    positionDeltas: diffOrder(startOrder, work),
    modifiers,
    dice: diceRecords,
    description,
    finderAttempts,
    driverId: extra.driverId ?? null,
    driverName: extra.driverName ?? null,
    from: extra.from ?? null,
    to: extra.to ?? null,
    subGroup: extra.subGroup ?? null,
    awardedBgChip: extra.awardedBgChip ?? false,
    bgChipRefused: extra.bgChipRefused ?? null,
    escapedWithoutGrade: extra.escapedWithoutGrade ?? false,
    passed: extra.passed ?? [],
    order: [...work],
    finalOrder: work.map((c) => c.card.driverId),
    occurred: extra.occurred,
    abortReason: extra.abortReason ?? null,
  });

  // ── NOTHING TO SURGE ──────────────────────────────────────────────────────
  //
  // "All cars in BG, including the LDG sub-group are eligible." With no BOTTOM
  // group at all — a field worn down to nineteen cars or fewer — there is nobody
  // the finder could name, and four rolls of it would be four rolls spent
  // discovering that. Checked BEFORE any die, because a rule that cannot happen
  // must not consume dice or it repaints every seeded race behind it.
  const bgCars = bottomGroupCount(work);
  if (bgCars === 0) {
    modifiers.push({ name: 'BOTTOM group cars', value: 0, detail: CITE_BGS_ELIGIBLE });
    return finish('surge.aborted', 'There is no bottom group left to surge out of.', {
      occurred: false,
      abortReason: 'no cars in the BOTTOM group',
    });
  }

  // ── THE DRIVER FINDER ─────────────────────────────────────────────────────
  //
  // "Use the Driver Finder Table to select the surging driver. If the Driver
  // Finder Table selects a grid position empty due to race attrition, re-roll on
  // the appropriate table. If three re-rolls fail to select an occupied BG grid
  // position the surge does not occur."
  //
  // "the appropriate table" is the BOTTOM GROUP one throughout: the Surge is
  // scoped to the BG by the eligibility sentence, and the failure clause says
  // "an occupied BG grid position". This is NOT the Restart Event's footnote,
  // which re-rolls in the next higher group — that sentence is printed there and
  // not here.
  //
  // ⚠ AN EXACT HIT IS REQUIRED. `rollDriverFinder` substitutes the nearest
  // occupied car when the named slot is empty, which is an engine ruling that is
  // right for a PROBLEM (something must happen to somebody) and wrong here,
  // because §3.13 prints the remedy itself: re-roll. `substituted` is exactly
  // "the named slot was empty", so it is read as a rejection.
  let chosen: Car | null = null;
  let chosenPosition = 0;
  for (let attempt = 1; attempt <= BGS_FINDER_MAX_ROLLS; attempt++) {
    const finder = rollDriverFinder('bottom', fieldOf(work), dice);
    diceRecords.push(finder.dice);
    const exact = finder.car !== null && !finder.substituted;
    const namedId = exact ? (finder.car?.driverId ?? null) : null;
    finderAttempts.push({
      attempt,
      code: finder.code,
      targetPosition: finder.targetPosition,
      driverId: namedId,
      accepted: exact,
    });
    if (exact && finder.car) {
      const i = work.findIndex((c) => c.card.driverId === finder.car?.driverId);
      if (i !== -1) {
        chosen = work[i];
        chosenPosition = i + 1;
        modifiers.push({
          name: 'BOTTOM GROUP DRIVER FINDER',
          value: finder.code,
          detail: `${finder.slot} → P${chosenPosition} (${CITE_BGS_FINDER})`,
        });
        break;
      }
    }
    modifiers.push({
      name: `finder roll ${attempt} rejected`,
      value: `P${finder.targetPosition} empty`,
      detail: CITE_BGS_FINDER,
    });
  }

  if (!chosen) {
    return finish(
      'surge.aborted',
      'The surge never finds its man — four looks at the bottom group and every slot the table ' +
        'points at is an empty space where a car used to be.',
      {
        occurred: false,
        abortReason: `${BGS_FINDER_REROLLS} re-rolls failed to select an occupied BG grid position`,
      },
    );
  }

  const driverId = chosen.card.driverId;
  const driverName = nameOf(chosen);
  const lapped = chosen.lapsDown === true;

  // ── THE LDSG BRANCH ───────────────────────────────────────────────────────
  //
  // "If the driver is in the LDSG he surges to escape the LDSG becoming the last
  // car in the LLSG. All LDSG drivers passed by the surging driver displace back
  // one position to fill the empty space. If the surging driver was in the LDSG
  // top position simply move the LDSG marker to the car occupying the second
  // LDSG position."
  //
  // `returnToLeadLap` is all three sentences at once, and it is the same helper
  // the Lucky Dog and the laps-down challenge escape use — the marker is not a
  // stored value but the boundary between the last lead-lap card and the first
  // lapped one, so moving the card moves the marker. One implementation of the
  // escape, three rules that need it.
  //
  // D-EZ-37 — no PIT CREW gate. See the header.
  if (lapped) {
    const before = work.map((c) => c.card.driverId);
    const delta = returnToLeadLap(work, driverId, 'surged out of the laps-down sub-group');
    const to = delta?.to ?? chosenPosition;
    const passed = before.slice(to - 1, chosenPosition - 1).filter((id) => id !== driverId);
    // ⚠ THE TRACKER MUST BE TOLD, and the first version of this file forgot.
    // `LapsDownTracker` remembers the turn each car went down (for "must spend at
    // least one complete turn in lap down status" before a Lucky Dog) and any
    // grade-D challenge wins it has banked. A car who surged back onto the lead
    // lap and was left in those maps would stay LUCKY DOG ELIGIBLE while running
    // on the lead lap, and would carry a stale banked win into his next trip
    // down. `returnToLeadLap` moves the CARD; only the tracker can forget the
    // history. Both other escapes — the Lucky Dog and the §3.11 challenge — call
    // this, which is exactly why the omission was worth a test of its own.
    input.lapsDown?.cameBack(driverId);
    // ESCAPE_CHALLENGES[grade] === null is grade E: "E can never challenge out of
    // the LDSG". He is not challenging, so he is not barred — counted, not
    // blocked, so the cost of D-EZ-37 is a number the harness can print.
    const escapedWithoutGrade = chosen.card.pitCrewGrade === 'E';
    modifiers.push({ name: 'LDSG escape', value: `P${chosenPosition} → P${to}`, detail: CITE_BGS_LDSG });
    if (escapedWithoutGrade) {
      modifiers.push({
        name: 'PIT CREW grade E surged out',
        value: 'no ladder on a Surge',
        detail: CITE_BGS_LDSG,
      });
    }
    return finish(
      'surge.ldsg',
      `${driverName} surges out of the laps-down group and gets his lap back — last car on the lead lap, ` +
        `and back in the race.`,
      {
        driverId,
        driverName,
        from: chosenPosition,
        to,
        subGroup: 'LDSG',
        escapedWithoutGrade,
        passed,
        occurred: true,
      },
    );
  }

  // ── ALREADY ON THE BG LEAD POSITION ───────────────────────────────────────
  //
  // "If the driver occupies the 20th position he is awarded a BG performance
  // chip." Printed in italics inside the LLSG paragraph, and it is the only
  // sentence §3.13 has about a chip — so it is deliberately NOT extended to the
  // LDSG branch above, where a car can also finish on 20th if the lead-lap
  // sub-group happens to be that short. A rule that hands out performance chips
  // is not one to widen by inference; §3.12 makes the same distinction for the
  // Porch.
  //
  // Note the shape of the rule: the Surge is "run to the front of the bottom
  // group", so a car already there has nowhere to run and is paid in a chip
  // instead. That is the same trade §3.12's Porch makes for the same car in the
  // same seat, which is a decent sign the reading is right.
  if (chosenPosition === BG_LEAD_POSITION) {
    const given = awardBgChip(chosen, chosenPosition);
    if (given.awarded > 0) {
      work[chosenPosition - 1] = given.car;
      modifiers.push({ name: 'BG performance chip', value: +given.awarded, detail: CITE_BGS_CHIP });
    }
    if (given.refused) {
      modifiers.push({ name: 'BG chip refused', value: given.refused, detail: CITE_BGS_CHIP });
    }
    // An award refused at the cap is still an OCCURRENCE: the rule ran, the
    // finder named a car, and §3.1 is what stopped the chip. Calling it an abort
    // would file a printed rule firing as a rule not firing.
    return finish(
      'surge.chip',
      given.awarded > 0
        ? `${driverName} is already the best car in the bottom group, and the surge pays him in a chip instead ` +
            `of a position.`
        : `${driverName} surges on the porch with his hands already full — no chip to be had.`,
      {
        driverId,
        driverName,
        from: chosenPosition,
        to: chosenPosition,
        subGroup: 'LLSG',
        awardedBgChip: given.awarded > 0,
        bgChipRefused: given.refused,
        occurred: true,
      },
    );
  }

  // ── THE LLSG BRANCH ───────────────────────────────────────────────────────
  //
  // "If the driver is in the LLSG he surges to the BG lead position (20th). All
  // drivers passed by the surging driver displace back one position to fill the
  // empty space."
  //
  // Lift the card out and put it back in at 20th; every card it passed slides
  // back one, which is what a splice does by construction. No gap is created and
  // no chips change scope — every position involved is inside the BOTTOM group,
  // because an LLSG car in the BOTTOM group sits at or above the laps-down
  // boundary and the destination is the top of the group.
  const passed = work.slice(BG_LEAD_POSITION - 1, chosenPosition - 1).map((c) => c.card.driverId);
  const [card] = work.splice(chosenPosition - 1, 1);
  work.splice(BG_LEAD_POSITION - 1, 0, card);
  modifiers.push({
    name: 'surges to the BG lead position',
    value: `P${chosenPosition} → P${BG_LEAD_POSITION}`,
    detail: CITE_BGS_LLSG,
  });
  modifiers.push({ name: 'cars displaced back one', value: passed.length, detail: CITE_BGS_LLSG });

  return finish(
    'surge.llsg',
    `${driverName} surges — ${chosenPosition - BG_LEAD_POSITION} position` +
      `${chosenPosition - BG_LEAD_POSITION === 1 ? '' : 's'} through the bottom group and onto the porch at ` +
      `${BG_LEAD_POSITION}th.`,
    {
      driverId,
      driverName,
      from: chosenPosition,
      to: BG_LEAD_POSITION,
      subGroup: 'LLSG',
      passed,
      occurred: true,
    },
  );
}

/**
 * BG chips held across a whole field. For the harness and for the invariant
 * check that no car outside the BOTTOM group is holding one.
 */
export function bgChipsInPlay(order: readonly Car[]): { driverId: string; position: number; chips: number }[] {
  const out: { driverId: string; position: number; chips: number }[] = [];
  order.forEach((car, i) => {
    const chips = bgChipsOf(car);
    if (chips > 0) out.push({ driverId: car.card.driverId, position: i + 1, chips });
  });
  return out;
}
